import { defineComponent, h, ref, useAttrs } from 'vue'
import { VAutocomplete, VCheckboxBtn, VListItem } from 'vuetify/components'
import {
  hasBooleanishAttr,
  normalizeLegacyMenuProps,
  resolveBgColor,
  resolveDensity,
  resolveVariant,
  stripLegacyInputAttrs,
  withLegacyDirtyClass
} from './legacyInputProps'
import { normalizeItemScope } from './VSelectCompat'

VAutocomplete.compatConfig = {
  ...(VAutocomplete.compatConfig || {}),
  COMPONENT_V_MODEL: false,
  INSTANCE_ATTRS_CLASS_STYLE: 'suppress-warning'
}

function getLegacyItemSelector(attrs = {}, camelName, kebabName, fallback) {
  return attrs[camelName] ?? attrs[kebabName] ?? fallback
}

function getItemProperty(item, selector, fallback) {
  if (item == null) return fallback

  let result
  if (typeof selector === 'function') {
    result = selector(item, fallback)
  } else if (Array.isArray(selector)) {
    result = selector.reduce(
      (value, key) => (value == null ? value : value[key]),
      item
    )
  } else if (typeof selector === 'string' && selector) {
    result = selector.split('.').reduce((value, key) => {
      return value == null ? value : value[key]
    }, item)
  }

  // Vuetify 3's default item-title/item-value key is 'title'/'value',
  // whereas Vuetify 2 defaulted to 'text'/'value'. Components that never
  // explicitly set item-text/item-title (relying on Vue 2's old default)
  // would otherwise resolve to undefined here and fall through to
  // Vuetify's own JSON-stringified-object display fallback.
  return result ?? fallback
}

function normalizeItem(item, attrs = {}) {
  if (item == null || typeof item !== 'object' || Array.isArray(item)) {
    return item
  }

  const titleSelector = getLegacyItemSelector(
    attrs,
    'itemTitle',
    'item-text',
    attrs.itemText ?? 'title'
  )
  const valueSelector = getLegacyItemSelector(
    attrs,
    'itemValue',
    'item-value',
    attrs.itemValue ?? 'value'
  )
  const disabledSelector = getLegacyItemSelector(
    attrs,
    'itemDisabled',
    'item-disabled'
  )
  const title = getItemProperty(
    item,
    titleSelector,
    item.title ?? item.text ?? item.label ?? item.value
  )
  const value = getItemProperty(
    item,
    valueSelector,
    item.value ?? item.key ?? item.id
  )
  const disabled =
    disabledSelector == null
      ? undefined
      : getItemProperty(item, disabledSelector, item.disabled)

  return {
    ...item,
    title,
    value,
    key: item.key ?? value ?? item.id,
    ...(disabled === undefined
      ? {}
      : {
          props: {
            ...(item.props || {}),
            disabled
          },
          disabled
        })
  }
}

function normalizeItems(items, attrs) {
  return Array.isArray(items)
    ? items.map((item) => normalizeItem(item, attrs))
    : items
}

function normalizeModelValue(attrs) {
  return attrs.modelValue ?? attrs.value
}

function normalizeSearchValue(attrs) {
  return attrs.search ?? attrs.searchInput ?? attrs['search-input']
}

function stripAutocompleteLegacyAttrs(attrs) {
  const normalized = stripLegacyInputAttrs(attrs)
  const menuProps = normalizeLegacyMenuProps(attrs)

  if (menuProps) {
    normalized.menuProps = menuProps
  }

  delete normalized.value
  if (attrs['item-text'] != null && normalized.itemTitle == null) {
    normalized.itemTitle = attrs['item-text']
  }
  if (attrs.itemText != null && normalized.itemTitle == null) {
    normalized.itemTitle = attrs.itemText
  }
  delete normalized.itemText
  delete normalized['item-text']
  if (attrs['item-value'] != null && normalized.itemValue == null) {
    normalized.itemValue = attrs['item-value']
  }
  if (attrs.itemValue != null && normalized.itemValue == null) {
    normalized.itemValue = attrs.itemValue
  }
  delete normalized.itemValue
  delete normalized['item-value']
  if (
    (attrs['item-disabled'] != null || attrs.itemDisabled != null) &&
    normalized.itemProps == null
  ) {
    const disabledSelector = attrs.itemDisabled ?? attrs['item-disabled']
    normalized.itemProps = (item) => ({
      disabled: getItemProperty(item, disabledSelector, item?.disabled)
    })
  }
  delete normalized.itemDisabled
  delete normalized['item-disabled']
  delete normalized.searchInput
  delete normalized['search-input']
  delete normalized['menu-props']
  return normalized
}

function normalizeSlotItem(item) {
  const raw = item?.raw ?? item
  if (raw && typeof raw === 'object') {
    return {
      ...item,
      ...raw,
      raw,
      text: raw.text ?? raw.title ?? item?.title,
      value: raw.value ?? raw.key ?? item?.value
    }
  }

  return raw
}

function renderDefaultItemLabel(item) {
  if (item == null) return ''
  if (typeof item !== 'object') return String(item)

  return item.text ?? item.title ?? item.label ?? item.value ?? item.key ?? ''
}

export default defineComponent({
  name: 'VAutocompleteCompat',
  compatConfig: {
    RENDER_FUNCTION: false,
    COMPONENT_V_MODEL: false,
    INSTANCE_ATTRS_CLASS_STYLE: false
  },
  inheritAttrs: false,
  emits: [
    'update:modelValue',
    'update:search',
    'update:searchInput',
    'change',
    'input'
  ],

  setup(_, { emit, slots }) {
    const attrs = useAttrs()
    let lastExternalModelValue
    let lastExternalSearchValue
    const localModelValue = ref()
    const localSearchValue = ref()

    return () => {
      const normalizedAttrs = withLegacyDirtyClass(
        stripAutocompleteLegacyAttrs(attrs),
        attrs
      )
      const variant = resolveVariant(attrs)
      const density = resolveDensity(attrs)
      const bgColor = resolveBgColor(attrs)
      const externalModelValue = normalizeModelValue(attrs)
      const externalSearchValue = normalizeSearchValue(attrs)

      if (lastExternalModelValue !== externalModelValue) {
        lastExternalModelValue = externalModelValue
        localModelValue.value = externalModelValue
      } else if (localModelValue.value === undefined) {
        localModelValue.value = externalModelValue
      }

      if (lastExternalSearchValue !== externalSearchValue) {
        lastExternalSearchValue = externalSearchValue
        localSearchValue.value = externalSearchValue
      } else if (localSearchValue.value === undefined) {
        localSearchValue.value = externalSearchValue
      }

      delete normalizedAttrs.onInput
      delete normalizedAttrs.onChange
      delete normalizedAttrs['onUpdate:modelValue']
      delete normalizedAttrs['onUpdate:search']
      delete normalizedAttrs['onUpdate:searchInput']

      return h(
        VAutocomplete,
        {
          ...normalizedAttrs,
          ...(variant ? { variant } : {}),
          ...(density ? { density } : {}),
          ...(bgColor ? { bgColor } : {}),
          items: normalizeItems(attrs.items, attrs),
          modelValue: localModelValue.value,
          search: localSearchValue.value,
          'onUpdate:modelValue': (value) => {
            if (value === localModelValue.value) return
            localModelValue.value = value
            emit('update:modelValue', value)
            emit('change', value)
            emit('input', value)
          },
          'onUpdate:search': (value) => {
            if (value === localSearchValue.value) return
            localSearchValue.value = value
            emit('update:search', value)
            emit('update:searchInput', value)
          }
        },
        {
          ...slots,
          item: slots.item
            ? (scope) =>
                slots.item(normalizeItemScope(scope, localModelValue.value))
            : (scope) => {
                // Vuetify 4's own uncompatted VAutocomplete auto-prepends a
                // checkbox to each item when `multiple` is set (see
                // VAutocomplete.js's native item fallback). This compat
                // layer's item slot always intercepts that fallback, so
                // callers relying on the auto-checkbox (any `multiple`
                // legacy select with no custom v-slot:item) silently lost
                // it. Replicate it here so multi-select items keep their
                // checkbox without every call site having to draw its own.
                const prependSlot = hasBooleanishAttr(attrs.multiple)
                  ? {
                      prepend: ({ isSelected }) =>
                        h(VCheckboxBtn, {
                          modelValue: isSelected,
                          ripple: false,
                          tabindex: -1,
                          'aria-hidden': true,
                          onClick: (event) => event.preventDefault()
                        })
                    }
                  : {}

                return scope.props?.title != null
                  ? h(VListItem, scope.props, prependSlot)
                  : h(VListItem, scope.props, {
                      ...prependSlot,
                      default: () =>
                        renderDefaultItemLabel(normalizeSlotItem(scope.item))
                    })
              },
          selection: slots.selection
            ? (scope) =>
                slots.selection({
                  ...scope,
                  item: normalizeSlotItem(scope?.item)
                })
            : undefined
        }
      )
    }
  }
})
